const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const transformApiDataToChartFormat = (apiResponse = {}) => {
  const { Date: dates = [], Open = [], High = [], Low = [], Close = [], Volume = [] } = apiResponse;

  if (!Array.isArray(dates) || dates.length === 0) {
    return [];
  }

  const bars = dates.map((date, index) => {
    const parsedDate = toDate(date);
    if (!parsedDate) return null;

    return {
      date: parsedDate,
      open: Number(Open[index]) || 0,
      high: Number(High[index]) || 0,
      low: Number(Low[index]) || 0,
      close: Number(Close[index]) || 0,
      volume: Number(Volume[index]) || 0,
      chart: [],
      indicators: [],
    };
  });

  return bars.filter(Boolean).sort((a, b) => a.date - b.date);
};

export const mergeChartData = (existing = [], incoming = []) => {
  if (!Array.isArray(existing) || existing.length === 0) {
    return [...incoming];
  }

  if (!Array.isArray(incoming) || incoming.length === 0) {
    return [...existing];
  }

  const mergedMap = new Map();

  const upsert = (bar) => {
    if (!bar || !bar.date) return;
    const key = toDate(bar.date)?.getTime();
    if (key == null) return;

    // Check if bar already exists in map
    const existingBar = mergedMap.get(key);
    if (existingBar) {
      // Merge the bars, preserving indicators and chart data from existing
      mergedMap.set(key, {
        ...bar,
        date: new Date(key),
        // Preserve existing indicators if the new bar doesn't have them
        indicators: bar.indicators && bar.indicators.length > 0
          ? bar.indicators
          : existingBar.indicators || [],
        // Preserve existing chart (overlay) data if the new bar doesn't have it
        chart: bar.chart && bar.chart.length > 0
          ? bar.chart
          : existingBar.chart || [],
      });
    } else {
      mergedMap.set(key, { ...bar, date: new Date(key) });
    }
  };

  existing.forEach(upsert);
  incoming.forEach(upsert);

  return Array.from(mergedMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([, value]) => value);
};

export const getOldestTimestamp = (data = []) => {
  if (!Array.isArray(data) || data.length === 0) return null;
  const oldest = data.reduce((acc, item) => {
    const ts = toDate(item?.date)?.getTime();
    if (ts == null) return acc;
    return acc == null || ts < acc ? ts : acc;
  }, null);
  return oldest;
};

export const formatTimestampForApi = (timestamp) => {
  const date = typeof timestamp === "number" ? new Date(timestamp) : toDate(timestamp);
  if (!date) return "";
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// How many rows to ask the indicator/overlay service for.
//
// The service filters by `start_date` and then takes `.head(limit)` — the
// OLDEST `limit` rows, not the newest. So asking for too few does not trim the
// history, it trims TODAY: the line renders in the past and stops before the
// live bars, which is what the client saw as "the data stops and nothing is
// current". Over-asking costs nothing because the filter runs before head(),
// so the budget is deliberately far above any real bar count.
//
// This lives here, alone, on purpose. The last time a number like this was
// copied into several files they drifted apart and a bug hid in the gap.
export const INDICATOR_ROW_BUDGET = 5000;

export const indicatorRowBudget = (barCount = 0) =>
  Math.max(barCount * 3, INDICATOR_ROW_BUDGET);
